import { SocialExportGetConnectionStateDto } from '@mi-front/api-v2';
import { useQueryParam } from '@mi-front/core';
import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Manages which connection is selected on the manage page.
 * Supports deep-link via `connectionId` query param (including post-OAuth redirect)
 * and auto-selects the first connection when none is specified.
 */
export function useConnectionsManageSelection(connections: SocialExportGetConnectionStateDto[]) {
  const history = useHistory();
  const location = useLocation();
  const connectionIdFromQuery = useQueryParam('connectionId');

  const selectedConnectionId = useMemo(() => {
    if (connectionIdFromQuery && connections.some((c) => c.id === connectionIdFromQuery)) {
      return connectionIdFromQuery;
    }
    return connections[0]?.id ?? null;
  }, [connectionIdFromQuery, connections]);

  const selectedConnection = useMemo(
    () => connections.find((c) => c.id === selectedConnectionId) ?? null,
    [connections, selectedConnectionId],
  );

  /** Sync URL when auto-selecting or when query points to a missing connection. */
  useEffect(() => {
    if (!selectedConnectionId) return;
    if (connectionIdFromQuery === selectedConnectionId) return;

    const params = new URLSearchParams(location.search);
    params.set('connectionId', selectedConnectionId);
    history.replace({ pathname: location.pathname, search: params.toString() });
  }, [selectedConnectionId, connectionIdFromQuery, history, location.pathname, location.search]);

  const selectConnection = useCallback(
    (connectionId: string) => {
      const params = new URLSearchParams(location.search);
      params.set('connectionId', connectionId);
      // Keep connectResult if present so the success alert can still show once.
      history.replace({ pathname: location.pathname, search: params.toString() });
    },
    [history, location.pathname, location.search],
  );

  return { selectedConnectionId, selectedConnection, selectConnection };
}
